import React, { useRef, useState } from 'react';
import MarkdownView from '../../components/common/MarkdownView';

export const MAX_MARKDOWN_BYTES = 500 * 1024;

export interface Insert {
  before: string;
  after?: string;
  placeholder: string;
  /** Insert `before` at the start of the current line instead of around the selection. */
  linePrefix?: boolean;
}

const TOOLBAR: { label: string; title: string; insert: Insert }[] = [
  { label: 'B', title: 'Bold', insert: { before: '**', after: '**', placeholder: 'bold text' } },
  { label: 'H', title: 'Heading', insert: { before: '## ', placeholder: 'Heading', linePrefix: true } },
  { label: '•', title: 'Bulleted list', insert: { before: '- ', placeholder: 'List item', linePrefix: true } },
  { label: 'Link', title: 'Link', insert: { before: '[', after: '](https://)', placeholder: 'link text' } },
  { label: '</>', title: 'Code', insert: { before: '`', after: '`', placeholder: 'code' } },
];

export function applyInsert(value: string, start: number, end: number, insert: Insert): { value: string; cursor: number } {
  if (insert.linePrefix) {
    const lineStart = start === 0 ? 0 : value.lastIndexOf('\n', start - 1) + 1;
    const lineIsEmpty = value.slice(lineStart).split('\n')[0] === '';
    const text = lineIsEmpty ? insert.placeholder : '';
    const next = value.slice(0, lineStart) + insert.before + text + value.slice(lineStart);
    return { value: next, cursor: end + insert.before.length + text.length };
  }
  const selected = value.slice(start, end) || insert.placeholder;
  const next = value.slice(0, start) + insert.before + selected + (insert.after ?? '') + value.slice(end);
  return { value: next, cursor: start + insert.before.length + selected.length };
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

interface MarkdownEditorProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  downloadName: string;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ id, label, value, onChange, disabled = false, downloadName }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState('');

  const handleInsert = (insert: Insert) => {
    const el = textareaRef.current;
    if (!el) return;
    const result = applyInsert(value, el.selectionStart, el.selectionEnd, insert);
    onChange(result.value);
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(result.cursor, result.cursor);
    });
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!/\.(md|markdown)$/i.test(file.name)) {
      setUploadError('Choose a .md file.');
      return;
    }
    if (file.size > MAX_MARKDOWN_BYTES) {
      setUploadError('File is larger than 500 KB.');
      return;
    }
    setUploadError('');
    try {
      onChange(await readFileAsText(file));
    } catch {
      setUploadError('Could not read that file.');
    }
  };

  const handleDownload = () => {
    const url = URL.createObjectURL(new Blob([value], { type: 'text/markdown' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadName;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="md-editor">
      <div className="md-editor__header">
        <label htmlFor={id} className="manage-field__label">{label}</label>
        <div className="md-editor__toolbar" role="toolbar" aria-label={`${label} formatting`}>
          {TOOLBAR.map((item) => (
            <button
              key={item.title}
              type="button"
              title={item.title}
              aria-label={item.title}
              disabled={disabled}
              onClick={() => handleInsert(item.insert)}
            >
              {item.label}
            </button>
          ))}
          <button type="button" disabled={disabled} onClick={() => fileRef.current?.click()}>Upload .md</button>
          <input
            ref={fileRef}
            type="file"
            accept=".md,.markdown,text/markdown"
            hidden
            aria-label="Upload Markdown file"
            onChange={handleUpload}
          />
          <button type="button" onClick={handleDownload}>Download</button>
        </div>
      </div>
      {uploadError && <p className="manage-error" role="alert">{uploadError}</p>}
      <div className="md-editor__panes">
        <textarea
          id={id}
          ref={textareaRef}
          className="md-editor__source"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          spellCheck
        />
        <div className="md-editor__preview" aria-label={`${label} preview`}>
          {value.trim() ? <MarkdownView>{value}</MarkdownView> : <p className="md-editor__empty">Nothing to preview yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default MarkdownEditor;
